import { MonetaryInput } from "$root/components/monetary-input";
import { currencyCode, formatCurrencyAmount } from "$root/utils/currency";
import type { Currencies } from "@eu.mdotm.api/portfolio-lab";
import type { MinimumDialogProps } from "$root/components/spawnable/type";
import { Button, FormField, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import { Dialog, DialogFooter, Text } from "@mdotm/shadcn/legacy/components";
import { type SpawnResult, adaptAnimatedNodeProvider, spawn } from "@mdotm/shadcn/legacy/react-extensions";
import { useState } from "react";
import { MAX_LAB_AUM } from "../utils/math";

type AumDialogProps = {
	currentAum: number | null;
	/** Currency whose ISO code the helper text and input adornment show; the platform default when unset. */
	currency: Currencies | null | undefined;
	onConfirm(newAum: number): void;
};

function AumDialog({
	show,
	onClose,
	onAnimationStateChange,
	currentAum,
	currency,
	onConfirm,
}: AumDialogProps & MinimumDialogProps) {
	const [value, setValue] = useState<number | null>(currentAum);
	// MAX_LAB_AUM caps USER INPUT only — API-provided AUM above the cap is never rejected
	// or clamped; weightPrecisionForAum degrades gracefully for out-of-range server values.
	const exceedsMax = value != null && value > MAX_LAB_AUM;
	const invalid = value == null || Number.isNaN(value) || value < 0 || exceedsMax;

	return (
		<Dialog
			show={show}
			onClose={onClose}
			header="Total portfolio value"
			onAnimationStateChange={onAnimationStateChange}
			footer={
				<DialogFooter
					neutralAction={
						<Button palette="tertiary" onClick={onClose}>
							Cancel
						</Button>
					}
					primaryAction={
						<SubmitButton disabled={invalid} data-qualifier="PortfolioLab/AumDialog/Confirm">
							Confirm
						</SubmitButton>
					}
				/>
			}
			onSubmitAsync={() => {
				if (value != null) {
					onConfirm(value);
				}
			}}
		>
			<Text type="Body/M/Book" className="mb-4" as="p">
				Set the total value in {currencyCode(currency)} to calculate monetary weights
			</Text>
			<FormField
				label=""
				error={
					exceedsMax
						? `The total portfolio value cannot exceed ${formatCurrencyAmount(MAX_LAB_AUM, currency, 0)}`
						: undefined
				}
			>
				<MonetaryInput
					value={value}
					onChange={setValue}
					min={0}
					max={MAX_LAB_AUM}
					currency={currency}
					data-qualifier="PortfolioLab/AumDialog/Input"
				/>
			</FormField>
		</Dialog>
	);
}

export function spawnAumDialog(props: AumDialogProps): SpawnResult<void> {
	return spawn<void>(
		adaptAnimatedNodeProvider(({ show, resolve, onHidden }) => (
			<AumDialog
				{...props}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve()}
				onConfirm={(newAum) => {
					props.onConfirm(newAum);
					resolve();
				}}
			/>
		)),
	);
}
